import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flame, ArrowUpRight, Mail, Linkedin, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import EngagementScoreBadge from "@/components/EngagementScoreBadge";
import ContactDetailDialog from "@/components/ContactDetailDialog";
import api from "@/lib/api";

/**
 * Dashboard widget — top 5 warm/hot contacts attached to signed Mandants.
 * Click on a row opens the ContactDetailDialog.
 * Quick action buttons: Mail (mailto), LinkedIn (open profile if URL present in detail dialog).
 */
export default function HotLeadsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({});
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/hot-leads?limit=5&min_score=0");
      setItems(data.items || []);
      setMeta({
        signed_principals: data.signed_principals || 0,
        total_matching: data.total_matching || 0,
      });
    } catch (_e) {
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <>
      <Card className="border-border" data-testid="hot-leads-widget">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-md bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
              <Flame className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
            </span>
            <CardTitle className="text-sm font-medium">
              {t("dashboard.hot_leads", "Hot leads à relancer")}
            </CardTitle>
            {meta.total_matching > 5 && (
              <Badge variant="outline" className="text-[10px]">
                +{meta.total_matching - 5}
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/contacts?sort=score")}
            data-testid="hot-leads-see-all"
          >
            {t("dashboard.see_all", "Tout voir")} <ArrowUpRight className="h-3 w-3 ml-1" />
          </Button>
        </CardHeader>

        <CardContent className="pt-1">
          {items === null ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-11 rounded bg-muted/40 animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-6" data-testid="hot-leads-empty">
              <div className="text-xs text-muted-foreground mb-1">
                {t("dashboard.hot_leads_empty_title", "Aucun lead chaud pour le moment")}
              </div>
              <div className="text-[11px] text-muted-foreground/70">
                {meta.signed_principals === 0
                  ? t("dashboard.hot_leads_empty_no_signed", "Marquez d'abord un Mandant comme « signed » dans Principals.")
                  : t("dashboard.hot_leads_empty_hint", "Un contact devient chaud après des ouvertures, clics ou réponses sur vos emails.")}
              </div>
            </div>
          ) : (
            <ul className="space-y-1">
              {items.map((c) => (
                <li
                  key={c.id}
                  className="group flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-muted/60 cursor-pointer transition-colors"
                  onClick={() => setSelected(c)}
                  data-testid={`hot-lead-${c.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-[13px] font-medium truncate">
                        {c.first_name} {c.last_name}
                      </div>
                      <EngagementScoreBadge score={c.engagement_score} tier={c.engagement_tier} size="xs" />
                      {c.verification_status === "GOLD_VERIFIED" && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1 h-4 rounded text-[8px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-300"
                          title="Apollo verified — données recoupées et confirmées"
                          data-testid={`hot-lead-apollo-${c.id}`}
                        >
                          <ShieldCheck className="h-2.5 w-2.5" /> Apollo
                        </span>
                      )}
                    </div>
                    <div className="text-[10.5px] text-muted-foreground truncate">
                      {c.position ? `${c.position} · ` : ""}
                      {c.company_name}
                      {c.principals?.length > 0 && (
                        <span className="text-muted-foreground/70">
                          {" "}· {t("dashboard.for", "pour")} {c.principals.map((p) => p.name).join(", ")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {c.email && (
                      <a
                        href={`mailto:${c.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="h-7 w-7 rounded flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-primary"
                        title={c.email}
                        data-testid={`hot-lead-mail-${c.id}`}
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {c.linkedin_url && (
                      <a
                        href={c.linkedin_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="h-7 w-7 rounded flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-primary"
                        title="LinkedIn"
                      >
                        <Linkedin className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <ContactDetailDialog
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        contact={selected}
        onUpdated={load}
      />
    </>
  );
}
